import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { data, error: functionError } = await supabase.functions.invoke(
        'send-password-reset-email',
        {
          body: {
            email: email,
            redirectUrl: `${window.location.origin}/reset-password`,
          },
        }
      );

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw new Error(functionError.message || 'Failed to send password reset email');
      }

      if (!data?.success) {
        if (data?.rateLimited) {
          throw new Error(data.error || 'Too many attempts. Please try again later.');
        }
        throw new Error(data?.error || 'Failed to send password reset email');
      }

      setEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      console.error('Password reset error:', error);

      if (error?.message?.includes('Failed to fetch') || error?.message?.includes('Network error')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error?.message?.includes('Too many')) {
        setError(error.message);
      } else if (error?.message) {
        setError(error.message);
      } else {
        setError('Failed to send password reset email. Please try again.');
      }

      toast.error(error?.message || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="py-12">
        <div className="container max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold">Check Your Email</h1>
            <p className="text-muted-foreground mt-2">
              We've sent password reset instructions
            </p>
          </div>

          <div className="bg-white rounded-xl border border-border/40 p-8">
            <div className="space-y-4">
              <div className="bg-green-50 text-green-700 rounded-md p-4">
                <p className="font-medium mb-2">Email sent to:</p>
                <p className="text-sm">{email}</p>
              </div>

              <div className="bg-blue-50 rounded-md p-4">
                <p className="text-sm font-medium text-blue-800 mb-2">Next steps:</p>
                <ol className="text-xs text-blue-700 space-y-2 list-decimal list-inside">
                  <li>Check your email inbox for a message from FutureLens</li>
                  <li>Click the password reset link in the email</li>
                  <li>Enter your new password</li>
                  <li>Sign in with your new password</li>
                </ol>
              </div>

              <div className="bg-amber-50 rounded-md p-4">
                <p className="text-xs text-amber-700">
                  <strong>Didn't receive the email?</strong> Check your spam folder or{' '}
                  <button
                    onClick={() => setEmailSent(false)}
                    className="underline hover:text-amber-800"
                  >
                    try again
                  </button>
                </p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-primary font-medium hover:underline flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="container max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Reset Your Password</h1>
          <p className="text-muted-foreground mt-2">
            Enter your email address and we'll send you instructions to reset your password
          </p>
        </div>

        <div className="bg-white rounded-xl border border-border/40 p-8">
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 mb-6 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending Reset Link...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-primary hover:underline flex items-center justify-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Sign In
            </Link>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary font-medium hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
